import { 
  WorkflowIcon, 
  DatabaseIcon, 
  WrenchIcon, 
  MicIcon, 
  ShieldCheckIcon, 
  CpuIcon, 
  ClockIcon, 
  ServerIcon 
} from '../components/Icons';

export const APP_CONFIG = {
  projectName: 'Cortex Orchestrator',
  engineName: 'Supreme Adventure Engine v3.2',
  tagline: 'Distributed Multi-Agent Orchestration & Autonomous Workflow Engine',
  domain: 'agentexpert.in',
  contactEmail: 'contact@agentexpert.in',
  studioUrl: process.env.REACT_APP_STUDIO_URL || '/auth',
};

export const AGENT_FLEET = [
  {
    type: 'PLANNER_AGENT',
    name: 'Planner Agent',
    role: 'DAG Decomposition & Goal Synthesis',
    badge: 'Core Orchestrator',
    color: 'from-violet-500 to-indigo-600',
    accentText: 'text-violet-400',
    description: 'Analyzes high-level enterprise requests, decomposes them into multi-agent directed acyclic graphs (DAGs), and determines step dependencies and parallel branches.',
    capabilities: [
      'Topological execution planning',
      'Dynamic parallel step branch detection',
      'Context dependency resolution',
      'Fallback path creation'
    ],
    sampleTask: 'Break down "Resolve order refund & notify customer via Gmail" into RAG, Tool, and Critic steps'
  },
  {
    type: 'RAG_AGENT',
    name: 'RAG Knowledge Agent',
    role: 'Hybrid Vector & Semantic Retrieval',
    badge: 'Context Grounding',
    color: 'from-sky-500 to-cyan-600',
    accentText: 'text-cyan-400',
    description: 'Grounds every decision in verified enterprise documentation using hybrid Elasticsearch vector + BM25 search across multi-tenant knowledge bases.',
    capabilities: [
      'Multi-tenant vector namespace filtering',
      'Document chunking & PDF table extraction',
      'Sub-50ms vector cosine similarity',
      'Zero hallucination factual citations'
    ],
    sampleTask: 'Query RefundPolicy and ProductCatalog knowledge bases for SLA thresholds'
  },
  {
    type: 'TOOL_AGENT',
    name: 'Tool Execution Agent',
    role: 'SaaS Transactions & API Invocation',
    badge: 'Transactional Execution',
    color: 'from-emerald-500 to-teal-600',
    accentText: 'text-emerald-400',
    description: 'Executes authenticated real-world operations against internal APIs, databases, payment gateways, and SaaS apps like Gmail, Jira, Sheets, Drive, and Slack.',
    capabilities: [
      'One-click Postman / OpenAPI schema onboarding',
      'MVEL response filtering & payload mapping',
      'Two-phase transaction commits',
      'HMAC token cryptographic signature'
    ],
    sampleTask: 'Execute razorpay.refund("SUB-882") and update Jira issue #IT-492'
  },
  {
    type: 'CRITIC_AGENT',
    name: 'Critic & Guardrail Agent',
    role: 'Fidelity Audit & Policy Validation',
    badge: 'Mandatory Gatekeeper',
    color: 'from-rose-500 to-pink-600',
    accentText: 'text-rose-400',
    description: 'Enforces strict validation before any output is finalized. Audits agent steps against business rules, data schemas, compliance boundaries, and user constraints.',
    capabilities: [
      'Mandatory pre-delivery validation',
      'Schema compliance & regex parameter check',
      'Hallucination and drift detection',
      'Automated retry signal generation'
    ],
    sampleTask: 'Verify that refund amount matches invoice exactly and policy allows waiver'
  },
  {
    type: 'CODE_AGENT',
    name: 'Code Execution Agent',
    role: 'Algorithmic Synthesis & Sandbox Exec',
    badge: 'Runtime Computing',
    color: 'from-amber-500 to-orange-600',
    accentText: 'text-amber-400',
    description: 'Generates and runs sandboxed computations, data transformation routines, SQL queries, and custom script adapters on the fly.',
    capabilities: [
      'Sandboxed Python/JS execution runtime',
      'Complex calculation and ledger math',
      'Dynamic JSON/XML ETL transformations',
      'Strict timeout and memory boundaries'
    ],
    sampleTask: 'Calculate prorated refund delta across 3 subscription tiers'
  },
  {
    type: 'MEMORY_AGENT',
    name: 'Memory & Context Agent',
    role: 'Stateful Session & Episodic Recall',
    badge: 'Long-Term State',
    color: 'from-blue-500 to-indigo-600',
    accentText: 'text-blue-400',
    description: 'Maintains conversational continuity, user preference state, and long-term organization memory across disparate sessions and channels.',
    capabilities: [
      'Cross-session episodic recall',
      'ThreadLocal tenant user profile store',
      'Key-value entity tracking',
      'Automated semantic summarization'
    ],
    sampleTask: 'Recall past customer support escalations and preferred payment methods'
  }
];

export const PILLARS = [
  {
    id: 'orchestration',
    Icon: WorkflowIcon,
    title: 'Distributed DAG Orchestrator',
    subtitle: 'Dynamic Multi-Agent Planning & Execution',
    description: 'Coordinate specialized AI agents in structured, deterministic dependency graphs with automatic parallelization and quality gates.',
    points: [
      { label: 'Dynamic Planning', desc: 'The Planner Agent decomposes complex business queries into a step-by-step DAG with explicit dependencies.' },
      { label: 'Parallel Execution', desc: 'Independent tasks (such as searching multiple KBs and fetching SaaS records) execute concurrently for sub-second latency.' },
      { label: 'Mandatory Critic Gate', desc: 'Every plan must pass through the Critic Agent to verify factual consistency and parameter safety before completion.' }
    ]
  },
  {
    id: 'rag',
    Icon: DatabaseIcon,
    title: 'Enterprise Hybrid RAG',
    subtitle: 'Elasticsearch + Multi-Tenant Vector Search',
    description: 'Ingest raw PDFs, technical docs, and database records into isolated vector spaces with guaranteed factual citation.',
    points: [
      { label: 'Multi-Tenant Isolation', desc: 'Strict ThreadLocal tenant filtering ensures proprietary enterprise data remains completely segregated at the index level.' },
      { label: 'Document & Table Parser', desc: 'Integrated PDF table/text extraction preserves tabular layout structures for financial reports and invoices.' },
      { label: 'Hybrid Scoring', desc: 'Combines BM25 lexical precision with dense vector semantics (text-embedding-3-small) to eliminate hallucinations.' }
    ]
  },
  {
    id: 'tools',
    Icon: WrenchIcon,
    title: 'SaaS Tool Registry & Postman Import',
    subtitle: 'Actionable Transaction Execution',
    description: 'Turn static AI conversations into real-world business transactions with built-in integrations and instant OpenAPI onboarding.',
    points: [
      { label: 'Built-in Integrations', desc: 'Native connectors for Gmail, Google Drive, Sheets, Slack, Jira, HubSpot, Twenty CRM, and payment processors.' },
      { label: 'One-Click Postman Onboarding', desc: 'Directly import Postman collections or OpenAPI specs to automatically generate schema-validated agent tools.' },
      { label: 'Secure MVEL Sandboxing', desc: 'Response filtering and validation scripts run in hardened sandboxes, preventing rogue tool side-effects.' }
    ]
  },
  {
    id: 'voice',
    Icon: MicIcon,
    title: 'Voice Bot Studio & Gateway',
    subtitle: 'Sub-500ms Real-Time Voice Streaming',
    description: 'Empower customer phone lines, sales call desks, and live operations with a bi-directional WebSocket voice agent gateway.',
    points: [
      { label: 'Low-Latency Streaming', desc: 'Ultra-low latency audio processing pipeline designed for natural, interruptible voice conversations.' },
      { label: 'Omnichannel Telephony', desc: 'Seamlessly bridge WebSockets with SIP, Twilio, and web browser audio inputs.' },
      { label: 'Agent Handoff', desc: 'Voice agents invoke the same distributed tool and RAG engine as text copilots for instant transaction resolution.' }
    ]
  }
];

export const FEATURES = [
  { 
    Icon: ShieldCheckIcon, 
    title: 'Zero-Trust Multi-Tenancy', 
    description: 'Complete data isolation across MongoDB and Elasticsearch backed by ThreadLocal context and X-Tenant-ID validation.' 
  },
  { 
    Icon: ClockIcon, 
    title: 'Sub-400ms Tool Execution', 
    description: 'High-throughput execution engine optimized for low-overhead microservice dispatch and instant WebSocket streaming.' 
  },
  { 
    Icon: CpuIcon, 
    title: 'LLM Provider Agnostic', 
    description: 'Deploy on Azure OpenAI, OpenAI (GPT-4o), Anthropic Claude 3.5, Gemini 1.5/2.0, DeepSeek, or private on-prem vLLM clusters.' 
  },
  { 
    Icon: ServerIcon, 
    title: 'Embeddable Chatbot SDK', 
    description: 'Drop-in script tag widget with customizable UI themes, session memory persistence, and token streaming support.' 
  }
];

export const SAAS_INTEGRATIONS = [
  { name: 'Gmail', category: 'Communication', icon: '✉️', desc: 'Search threads, compose & send verified emails' },
  { name: 'Google Drive', category: 'Storage', icon: '📁', desc: 'Search documents, upload reports & parse assets' },
  { name: 'Google Sheets', category: 'Spreadsheets', icon: '📊', desc: 'Read/write tabular data, update records' },
  { name: 'Slack', category: 'Collaboration', icon: '💬', desc: 'Send channel alerts & interactive agent blocks' },
  { name: 'Jira', category: 'Issue Tracking', icon: '🎯', desc: 'Create tickets, update sprint items, search bugs' },
  { name: 'HubSpot & CRM', category: 'Sales', icon: '👥', desc: 'Enrich contacts, sync deals, log sales calls' },
  { name: 'Stripe & Razorpay', category: 'Payments', icon: '💳', desc: 'Initiate refunds, verify balance & transactions' },
  { name: 'Elasticsearch', category: 'Vector Search', icon: '🔍', desc: 'Hybrid dense vector & BM25 retrieval' },
  { name: 'MongoDB', category: 'Database', icon: '🍃', desc: 'Multi-tenant document store & session state' },
  { name: 'Postman / OpenAPI', category: 'Custom Tools', icon: '🚀', desc: 'Auto-generate agent tools from API collections' },
];

export const SIMULATION_SCENARIOS = [
  {
    id: 'refund-ticket',
    title: 'E-Commerce Refund & Jira Bug Ticket',
    userQuery: 'Customer #C-409 reported duplicate charge of $149 for order #ORD-9821. Verify policy, issue refund via Stripe, and open a Jira bug ticket.',
    steps: [
      { step: 1, agent: 'PLANNER_AGENT', action: 'Generate DAG execution plan with 4 tasks & dependency gates', latency: '42ms' },
      { step: 2, agent: 'RAG_AGENT', action: 'Search "RefundPolicy" & "OrderDocs" KB for duplicate billing grace period (Eligible: YES)', latency: '88ms' },
      { step: 3, agent: 'TOOL_AGENT', action: 'Call stripe.refund_charge(chargeId="ch_9941", amount=14900)', latency: '210ms' },
      { step: 4, agent: 'TOOL_AGENT', action: 'Call jira.create_ticket(project="BUG", title="Duplicate checkout race condition #ORD-9821")', latency: '145ms' },
      { step: 5, agent: 'CRITIC_AGENT', action: 'Audit transaction ref, check Jira key (#BUG-104), and confirm compliance', latency: '65ms' }
    ],
    outcome: 'Successfully refunded $149.00 (Stripe ref: re_3Lkp29). Created Jira ticket BUG-104 with transaction logs attached.'
  },
  {
    id: 'voice-sales',
    title: 'Voice Bot Call & HubSpot Lead Sync',
    userQuery: 'Caller inquiring about Enterprise SLA and requesting a 100-seat demo quote with custom SSO.',
    steps: [
      { step: 1, agent: 'PLANNER_AGENT', action: 'Route audio stream to Voice Bot Studio, establish real-time session', latency: '28ms' },
      { step: 2, agent: 'RAG_AGENT', action: 'Retrieve SLA tiers and SSO architecture guidelines from EnterpriseDoc KB', latency: '75ms' },
      { step: 3, agent: 'CODE_AGENT', action: 'Compute tier discount matrix for 100 seats ($48/seat/mo)', latency: '35ms' },
      { step: 4, agent: 'TOOL_AGENT', action: 'Create HubSpot deal "Acme Corp - 100 Seats" & schedule calendar invite', latency: '190ms' },
      { step: 5, agent: 'CRITIC_AGENT', action: 'Verify calendar availability and quote currency formatting', latency: '52ms' }
    ],
    outcome: 'Live voice call resolved in 48s. Generated quote PDF, created HubSpot deal #HD-902, and sent Google Calendar invite.'
  },
  {
    id: 'insurance-audit',
    title: 'Insurance Policy & PDF Table Audit',
    userQuery: 'Process uploaded hospitalization claim PDF (Claim #CLM-551). Extract itemized bill table and compute coverage.',
    steps: [
      { step: 1, agent: 'PLANNER_AGENT', action: 'Construct document processing pipeline with OCR table extractor', latency: '38ms' },
      { step: 2, agent: 'RAG_AGENT', action: 'Extract itemized hospital table; query PolicyRules KB for ICU capping', latency: '120ms' },
      { step: 3, agent: 'CODE_AGENT', action: 'Apply deductible deduction ($250) and co-pay ratio (10%)', latency: '40ms' },
      { step: 4, agent: 'TOOL_AGENT', action: 'Update Claims DB with approved payout $4,120 and send SMS notification', latency: '180ms' },
      { step: 5, agent: 'CRITIC_AGENT', action: 'Verify math sum against hospital itemized total, certify audit trail', latency: '70ms' }
    ],
    outcome: 'Claim CLM-551 parsed with 100% table accuracy. Approved claim payout: $4,120. Audit stamp #AUDIT-884-OK.'
  }
];
